import { useRef, useState } from 'react';
import moment from 'moment';
import {
	Check,
	Pencil,
	Trash2,
	Clock,
	CalendarIcon,
	UserCircle2,
	MoreHorizontal,
	MessageSquare,
	RotateCcw,
	Repeat,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
	ActionSheet,
	SwipeableRow,
	type ActionSheetItem,
	type SwipeAction,
} from '@/components/mobile';
import {
	formatDueDate,
	formatDashboardDueDate,
	PRIORITY_CONFIG,
	PriorityFlag,
	RecurrenceBadge,
	AssigneeAvatar,
	RECURRENCE_LABELS,
	SNOOZE_PRESETS,
	getReminderAssignees,
} from './reminderHelpers';
import { useUserDisplayNames } from '@/hooks/useUserColorOptions';
import {
	ACTION_META,
	ActionIcon,
	personName,
} from '@/components/reminder-sequences/sequenceActions';

// "Done by Sam · 4 Nov, 9:12 am" on every completed reminder.
const completedLine = (reminder: Reminder) => {
	if (!reminder.isCompleted) return null;
	const who = reminder._completedBy ? personName(reminder._completedBy) : '';
	const when = reminder.completedAt ? moment(reminder.completedAt).format('lll') : '';
	if (!who && !when) return null;
	return [who ? `Done by ${who}` : 'Done', when].filter(Boolean).join(' · ');
};

// "New lead follow-up · Step 2 of 4" for sequence steps.
const sequenceLine = (reminder: Reminder) =>
	reminder.sequence
		? [
				reminder.sequence.sequenceName,
				`Step ${reminder.sequence.stepNumber} of ${reminder.sequence.stepCount}`,
			]
				.filter(Boolean)
				.join(' · ')
		: null;

// Call, Text and Email steps get a button that opens the customer's conversation.
const hasQuickAction = (reminder: Reminder) =>
	Boolean(
		reminder.sequence &&
			!reminder.isCompleted &&
			reminder._customer?._id &&
			['call', 'text', 'email'].includes(reminder.sequence.action)
	);
import type { Reminder } from '@/slices/remindersApi';

interface ReminderRowProps {
	reminder: Reminder;
	layout?: 'compact' | 'dashboard';
	isOverdue?: boolean;
	disabled?: boolean;
	showAssignee?: boolean;
	showCustomer?: boolean;
	selectable?: boolean;
	selected?: boolean;
	onSelectChange?: (checked: boolean) => void;
	onToggleComplete: (reminder: Reminder) => void;
	onEdit: (reminder: Reminder) => void;
	onSnooze: (reminder: Reminder, isoDate: string) => void;
	onDelete: (reminder: Reminder) => void;
	onOpenCustomer?: (customerId: string) => void;
	/** Call / Text / Email on sequence steps; opens the customer's conversation */
	onQuickAction?: (reminder: Reminder) => void;
}

const ReminderRow = ({
	reminder,
	layout = 'compact',
	isOverdue = false,
	disabled = false,
	showAssignee = true,
	showCustomer = true,
	selectable = false,
	selected = false,
	onSelectChange,
	onToggleComplete,
	onEdit,
	onSnooze,
	onDelete,
	onOpenCustomer,
	onQuickAction,
}: ReminderRowProps) => {
	const doneBy = completedLine(reminder);
	const stepLine = sequenceLine(reminder);
	const quickAction =
		onQuickAction && hasQuickAction(reminder) && reminder.sequence
			? ACTION_META[reminder.sequence.action]
			: null;
	const customer = reminder._customer;
	const displayNames = useUserDisplayNames();
	const assignees = getReminderAssignees(reminder).map((user) => {
		const name = user.name || user.username || 'Teammate';
		return { name, label: displayNames.get(user._id) || name };
	});
	const assigneeNames = assignees.map((assignee) => assignee.label);
	const assigneeLabel = assigneeNames.join(', ');
	const isDashboard = layout === 'dashboard';
	const [deleteOpen, setDeleteOpen] = useState(false);
	const actionsRef = useRef<HTMLButtonElement>(null);
	const priority = PRIORITY_CONFIG[reminder.priority || 'none'];
	const isMobile = useIsMobile();
	// Phone action sheets: the row's "…" menu, and Snooze on its own for the swipe action.
	const [sheet, setSheet] = useState<'actions' | 'snooze' | null>(null);

	const snoozeItems: ActionSheetItem[] = SNOOZE_PRESETS.map((preset) => ({
		id: preset.label,
		label: preset.label,
		icon: Clock,
		trailing: moment(preset.getDate()).calendar(null, {
			sameDay: 'LT',
			nextDay: '[Tomorrow] LT',
			nextWeek: 'ddd LT',
			sameElse: 'LTS',
		}),
		onSelect: () => onSnooze(reminder, preset.getDate()),
	}));

	const actionSections = [
		{
			items: [
				...(quickAction
					? [
							{
								id: 'quick',
								label: `${quickAction.label} ${customer?.name ?? ''}`.trim(),
								description: 'Opens the conversation',
								icon: quickAction.icon,
								onSelect: () => onQuickAction?.(reminder),
							},
						]
					: []),
				{
					id: 'complete',
					label: reminder.isCompleted ? 'Mark as not done' : 'Mark as done',
					icon: reminder.isCompleted ? RotateCcw : Check,
					onSelect: () => onToggleComplete(reminder),
				},
				...(!reminder.isCompleted
					? [{ id: 'snooze', label: 'Snooze', icon: Clock, page: 'snooze' }]
					: []),
				{
					id: 'edit',
					label: 'Edit reminder',
					icon: Pencil,
					onSelect: () => onEdit(reminder),
				},
				...(customer?._id && onOpenCustomer
					? [
							{
								id: 'customer',
								label: 'Open conversation',
								description: customer.name,
								icon: MessageSquare,
								onSelect: () => customer._id && onOpenCustomer(customer._id),
							},
						]
					: []),
			] as ActionSheetItem[],
		},
		{
			items: [
				{
					id: 'delete',
					label: 'Delete reminder',
					icon: Trash2,
					destructive: true,
					onSelect: () => setDeleteOpen(true),
				},
			] as ActionSheetItem[],
		},
	];

	const phoneSheets = isMobile ? (
		<>
			<ActionSheet
				open={sheet === 'actions'}
				onOpenChange={(open) => !open && setSheet(null)}
				title={reminder.text}
				description={formatDashboardDueDate(reminder.dateTime)}
				sections={actionSections}
				pages={[
					{
						id: 'snooze',
						title: 'Snooze until',
						sections: [{ items: snoozeItems }],
					},
				]}
			/>
			<ActionSheet
				open={sheet === 'snooze'}
				onOpenChange={(open) => !open && setSheet(null)}
				title="Snooze until"
				items={snoozeItems}
			/>
		</>
	) : null;

	const deleteDialogContent = (
		<AlertDialogContent onCloseAutoFocus={(event) => {
			if (isDashboard && !isMobile) {
				event.preventDefault();
				actionsRef.current?.focus();
			}
		}}>
			<AlertDialogHeader>
				<AlertDialogTitle>Delete reminder?</AlertDialogTitle>
				<AlertDialogDescription>
					This will permanently delete &quot;
					{reminder.text}&quot;. This cannot be undone.
				</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel>Cancel</AlertDialogCancel>
				<AlertDialogAction
					className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
					onClick={() => onDelete(reminder)}
				>
					Delete
				</AlertDialogAction>
			</AlertDialogFooter>
		</AlertDialogContent>
	);

	// Phones: an iOS Reminders-style row. Swipe right to complete, left to
	// snooze or delete; the same actions sit behind the visible "…" button.
	if (isDashboard && isMobile) {
		const overdue = isOverdue && !reminder.isCompleted;
		const hasPriority = Boolean(reminder.priority && reminder.priority !== 'none');
		const recurrence = reminder.recurrence?.rule;
		const leadingActions: SwipeAction[] = [
			{
				id: 'complete',
				label: reminder.isCompleted ? 'Undo' : 'Done',
				icon: reminder.isCompleted ? RotateCcw : Check,
				className: 'bg-emerald-600',
				onAction: () => onToggleComplete(reminder),
			},
		];
		const trailingActions: SwipeAction[] = [
			...(!reminder.isCompleted
				? [
						{
							id: 'snooze',
							label: 'Snooze',
							icon: Clock,
							className: 'bg-amber-500',
							onAction: () => setSheet('snooze'),
						},
					]
				: []),
			{
				id: 'delete',
				label: 'Delete',
				icon: Trash2,
				className: 'bg-red-600',
				onAction: () => setDeleteOpen(true),
			},
		];

		return (
			<>
				<SwipeableRow
					leadingActions={leadingActions}
					trailingActions={trailingActions}
					disabled={selectable || disabled}
				>
					<div
						data-reminder-id={reminder._id}
						className={cn('flex scroll-mt-28 items-start', selected && 'bg-accent/60')}
					>
						{selectable ? (
							<label className="flex size-12 shrink-0 cursor-pointer items-center justify-center">
								<Checkbox
									checked={selected}
									onCheckedChange={(c) => onSelectChange?.(Boolean(c))}
									disabled={disabled}
									className="size-[22px] rounded-full"
									aria-label={`Select ${reminder.text}`}
								/>
							</label>
						) : (
							<button
								type="button"
								onClick={() => onToggleComplete(reminder)}
								disabled={disabled}
								className="flex size-12 shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
								aria-label={reminder.isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
							>
								<span
									className={cn(
										'flex size-[22px] items-center justify-center rounded-full border-[1.5px] transition-colors',
										reminder.isCompleted
											? 'border-primary bg-primary'
											: overdue
												? 'border-destructive/60 dark:border-red-400/60'
												: 'border-muted-foreground/40'
									)}
								>
									{reminder.isCompleted && (
										<Check className="size-3.5 text-primary-foreground" strokeWidth={3} />
									)}
								</span>
							</button>
						)}

						<button
							type="button"
							onClick={() => (selectable ? onSelectChange?.(!selected) : onEdit(reminder))}
							disabled={disabled}
							aria-pressed={selectable ? selected : undefined}
							className="min-w-0 flex-1 select-none py-3 pr-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary active:opacity-70"
						>
							<span
								className={cn(
									'flex items-start gap-2 break-words text-[16px] leading-snug',
									reminder.isCompleted && 'text-muted-foreground line-through'
								)}
							>
								{reminder.sequence && (
									<ActionIcon action={reminder.sequence.action} size="sm" className="mt-px" />
								)}
								<span className="min-w-0">{reminder.text}</span>
							</span>
							{stepLine && (
								<span className="mt-0.5 block truncate text-[13px] leading-5 text-muted-foreground">
									{stepLine}
								</span>
							)}
							{doneBy && (
								<span className="mt-0.5 block truncate text-[13px] leading-5 text-muted-foreground">
									{doneBy}
								</span>
							)}
							<span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] leading-5 text-muted-foreground">
								<time
									dateTime={reminder.dateTime}
									className={cn(overdue && 'font-medium text-destructive dark:text-red-400')}
								>
									{formatDashboardDueDate(reminder.dateTime)}
								</time>
								{hasPriority && (
									<span className="inline-flex items-center gap-1">
										<span className={cn('size-1.5 shrink-0 rounded-full', priority.dot)} />
										{priority.label}
									</span>
								)}
								{recurrence && recurrence !== 'none' && (
									<span className="inline-flex items-center gap-1">
										<Repeat className="size-3" aria-hidden />
										{RECURRENCE_LABELS[recurrence]}
									</span>
								)}
							</span>
							{((showCustomer && customer?.name) || (showAssignee && assigneeLabel)) && (
								// The narrowest phones give the assignees their own line when both don't fit
								<span className="mt-0.5 flex min-w-0 items-center gap-3 text-[13px] leading-5 text-muted-foreground max-[359px]:flex-wrap max-[359px]:gap-y-0">
									{showCustomer && customer?.name && (
										<span className="inline-flex min-w-0 items-center gap-1">
											<UserCircle2 className="size-3.5 shrink-0 opacity-60" aria-hidden />
											<span className="truncate">{customer.name}</span>
										</span>
									)}
									{showAssignee && assigneeLabel && (
										// Up to three avatars and "Ana, Ben +3"; VoiceOver reads every name
										<span className="inline-flex min-w-0 shrink-[2] items-center gap-1.5">
											<span aria-hidden className="flex shrink-0 -space-x-0.5">
												{assignees.slice(0, 3).map((assignee, index) => (
													<AssigneeAvatar
														key={index}
														name={assignee.name}
														label={assignee.label}
														className="size-4 shrink-0 ring-1 ring-background"
													/>
												))}
											</span>
											<span aria-hidden className="truncate">
												{assigneeNames.slice(0, 2).join(', ')}
											</span>
											{assigneeNames.length > 2 && (
												<span aria-hidden className="shrink-0">
													+{assigneeNames.length - 2}
												</span>
											)}
											<span className="sr-only">Assigned to {assigneeLabel}</span>
										</span>
									)}
								</span>
							)}
							{reminder.notes && (
								<span className="mt-0.5 line-clamp-1 break-words text-[13px] leading-5 text-muted-foreground/90">
									{reminder.notes}
								</span>
							)}
						</button>

						{!selectable && (
							<button
								type="button"
								onClick={() => setSheet('actions')}
								disabled={disabled}
								className="flex size-12 shrink-0 items-center justify-center text-muted-foreground active:opacity-60"
								aria-label={`Actions for ${reminder.text}`}
								aria-haspopup="dialog"
							>
								<MoreHorizontal className="size-5" />
							</button>
						)}
					</div>
				</SwipeableRow>
				{phoneSheets}
				<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
					{deleteDialogContent}
				</AlertDialog>
			</>
		);
	}

	return (
		<div
			data-reminder-id={reminder._id}
			className={cn(
				'group flex gap-3 hover:bg-accent/40 transition-colors w-full',
				isDashboard ? 'items-center px-4 py-3.5' : 'items-start px-3 py-2.5',
				selected && 'bg-accent/40'
			)}
		>
			{selectable && (
				<Checkbox
					checked={selected}
					onCheckedChange={(c) => onSelectChange?.(Boolean(c))}
					disabled={disabled}
					className={isDashboard ? 'shrink-0' : 'mt-1'}
					aria-label="Select reminder"
				/>
			)}

			<button
				type="button"
				onClick={() => onToggleComplete(reminder)}
				disabled={disabled}
				className={cn(
					'flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-full max-md:p-3 touch:p-3',
					// A 44px hit area on phones and touch screens without moving the circle.
					isDashboard
						? 'max-md:-m-3 touch:-m-3'
						: 'mt-0.5 max-md:-mx-3 max-md:-mb-3 max-md:-mt-2.5 touch:-mx-3 touch:-mb-3 touch:-mt-2.5'
				)}
				aria-label={
					reminder.isCompleted
						? 'Mark as incomplete'
						: 'Mark as complete'
				}
			>
				<div
					className={cn(
						'h-5 w-5 rounded-full flex items-center justify-center transition-colors',
						isDashboard ? 'border-[1.5px]' : 'border-2',
						reminder.isCompleted
							? 'bg-primary border-primary'
							: 'border-muted-foreground/40 group-hover:border-primary/70'
					)}
				>
					{reminder.isCompleted && (
						<Check className="h-3 w-3 text-primary-foreground" />
					)}
				</div>
			</button>

			{isDashboard ? (
				<div className="flex min-w-0 flex-1 flex-col gap-2 lg:flex-row lg:items-center lg:gap-6">
					<div className="min-w-0 flex-1">
						<p className={cn(
							'flex items-center gap-2 text-sm font-medium break-words',
							reminder.isCompleted && 'line-through text-muted-foreground'
						)}>
							{reminder.sequence && (
								<ActionIcon action={reminder.sequence.action} size="sm" />
							)}
							<span className="min-w-0">{reminder.text}</span>
						</p>
						<div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
							{showAssignee && (
								<span
									className="inline-flex min-w-0 max-w-full items-center gap-1.5"
									title={assigneeLabel ? `Assigned to ${assigneeLabel}` : 'Unassigned'}
								>
									<AssigneeAvatar
										name={assignees[0]?.name}
										label={assignees[0]?.label}
										className="shrink-0"
									/>
									<span className="truncate">{assigneeLabel || 'Unassigned'}</span>
								</span>
							)}
							{showCustomer && customer?.name && (
								<button
									type="button"
									className="inline-flex min-w-0 max-w-full items-center gap-1 hover:text-foreground focus-visible:underline focus-visible:outline-none touch:-my-3.5 touch:py-3.5"
									onClick={() => customer._id && onOpenCustomer?.(customer._id)}
									disabled={!onOpenCustomer || disabled}
									title={`Open conversation with ${customer.name}`}
								>
									<UserCircle2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
									<span className="truncate">{customer.name}</span>
								</button>
							)}
							{stepLine && <span className="truncate">{stepLine}</span>}
							<RecurrenceBadge rule={reminder.recurrence?.rule} />
						</div>
						{doneBy && (
							<p className="mt-1 text-xs text-muted-foreground">{doneBy}</p>
						)}
						{reminder.notes && (
							<p className="mt-1 line-clamp-2 break-words text-xs text-muted-foreground">
								{reminder.notes}
							</p>
						)}
					</div>
					<div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs lg:flex-nowrap lg:gap-6">
						{quickAction && (
							<Button
								size="sm"
								variant="outline"
								className="h-7 gap-1.5 rounded-full px-3 text-xs touch:h-9"
								onClick={() => onQuickAction?.(reminder)}
								disabled={disabled}
								title="Opens the conversation"
							>
								<quickAction.icon className={cn('size-3.5', quickAction.tint)} aria-hidden />
								{quickAction.label}
							</Button>
						)}
						<span
							className="inline-flex items-center gap-1.5 lg:w-20"
							aria-label={`${priority.label} priority`}
						>
							<span className={cn(
								'h-1.5 w-1.5 shrink-0 rounded-full',
								reminder.priority === 'high' || reminder.priority === 'med'
									? priority.dot
									: 'bg-muted-foreground/45'
							)} />
							<span className="text-muted-foreground">
								{reminder.priority && reminder.priority !== 'none' ? priority.label : 'No priority'}
							</span>
						</span>
						<time
							dateTime={reminder.dateTime}
							className={cn(
								'lg:w-48 lg:text-right',
								isOverdue && !reminder.isCompleted ? 'text-destructive dark:text-red-400' : 'text-muted-foreground'
							)}
						>
							{formatDashboardDueDate(reminder.dateTime)}
						</time>
					</div>
				</div>
			) : (
				<div className="flex-grow min-w-0">
					<div className="flex items-center gap-1.5">
						<PriorityFlag priority={reminder.priority} />
						<p
							className={cn(
								'text-sm font-medium break-words min-w-0',
								isOverdue && !reminder.isCompleted && 'text-destructive',
								reminder.isCompleted &&
								'line-through text-muted-foreground'
							)}
						>
							{reminder.text}
						</p>
					</div>

					{reminder.notes && (
						<p className="text-xs text-muted-foreground mt-0.5 break-words line-clamp-2">
							{reminder.notes}
						</p>
					)}
					{doneBy && (
						<p className="text-xs text-muted-foreground mt-0.5">{doneBy}</p>
					)}

					<div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
						<span className="inline-flex items-center">
							<CalendarIcon className="h-3 w-3 mr-1 opacity-70" />
							{formatDueDate(reminder.dateTime)}
						</span>

						<RecurrenceBadge rule={reminder.recurrence?.rule} />

						{showCustomer && customer?.name && (
							<button
								type="button"
								className="inline-flex items-center hover:text-foreground max-w-[180px]"
								onClick={() =>
									customer._id && onOpenCustomer?.(customer._id)
								}
								disabled={!onOpenCustomer}
								title={customer.name}
							>
								<UserCircle2 className="h-3.5 w-3.5 mr-1 opacity-60 flex-shrink-0" />
								<span className="truncate">{customer.name}</span>
							</button>
						)}

						{showAssignee && assigneeNames.length > 0 && (
							<span
								className="inline-flex items-center"
								title={`Assigned to ${assigneeLabel}`}
							>
								<span aria-hidden className="inline-flex items-center -space-x-1">
									{assignees.slice(0, 3).map((assignee, index) => (
										<AssigneeAvatar
											key={index}
											name={assignee.name}
											label={assignee.label}
											className="ring-1 ring-background"
										/>
									))}
									{assigneeNames.length > 3 && (
										<span className="pl-1.5">+{assigneeNames.length - 3}</span>
									)}
								</span>
								{/* VoiceOver skips aria-label on a plain span, so the names are spelled out */}
								<span className="sr-only">Assigned to {assigneeLabel}</span>
							</span>
						)}
					</div>
				</div>
			)}

			<div className={cn(
				'flex items-center gap-0.5 flex-shrink-0',
				!isDashboard && 'opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity touch:opacity-100'
			)}>
				{!isDashboard && isMobile ? (
					<Button
						variant="ghost"
						size="icon"
						className="-my-2 -mr-2 size-11 rounded-full text-muted-foreground"
						onClick={() => setSheet('actions')}
						disabled={disabled}
						aria-label={`Actions for ${reminder.text}`}
						aria-haspopup="dialog"
					>
						<MoreHorizontal className="h-5 w-5" />
					</Button>
				) : isDashboard ? (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								ref={actionsRef}
								variant="ghost"
								size="icon"
								className="h-8 w-8 rounded-full text-muted-foreground touch:-m-1.5 touch:size-11"
								disabled={disabled}
								aria-label={`Actions for ${reminder.text}`}
							>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onSelect={() => onEdit(reminder)}>
								<Pencil />Edit reminder
							</DropdownMenuItem>
							{!reminder.isCompleted && (
								<DropdownMenuSub>
									<DropdownMenuSubTrigger>
										<Clock />Snooze until
									</DropdownMenuSubTrigger>
									<DropdownMenuPortal>
										<DropdownMenuSubContent>
											{SNOOZE_PRESETS.map((preset) => (
												<DropdownMenuItem
													key={preset.label}
													onSelect={() => onSnooze(reminder, preset.getDate())}
												>
													{preset.label}
												</DropdownMenuItem>
											))}
										</DropdownMenuSubContent>
									</DropdownMenuPortal>
								</DropdownMenuSub>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className="text-destructive focus:text-destructive dark:text-red-400 dark:focus:text-red-400"
								onSelect={() => setDeleteOpen(true)}
							>
								<Trash2 />Delete reminder
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				) : (
					<>
						{!reminder.isCompleted && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 touch:-my-1.5 touch:size-11"
										disabled={disabled}
										aria-label="Snooze"
									>
										<Clock className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuLabel>Snooze until</DropdownMenuLabel>
									<DropdownMenuSeparator />
									{SNOOZE_PRESETS.map((preset) => (
										<DropdownMenuItem
											key={preset.label}
											onClick={() =>
												onSnooze(reminder, preset.getDate())
											}
										>
											{preset.label}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						)}

						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 touch:-my-1.5 touch:size-11"
							onClick={() => onEdit(reminder)}
							disabled={disabled}
							aria-label="Edit"
						>
							<Pencil className="h-4 w-4" />
						</Button>
					</>
				)}

				<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
					{!isDashboard && !isMobile && (
						<AlertDialogTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7 touch:-my-1.5 touch:size-11 text-destructive hover:text-destructive hover:bg-destructive/10"
								disabled={disabled}
								aria-label="Delete"
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</AlertDialogTrigger>
					)}
					{deleteDialogContent}
				</AlertDialog>
			</div>
			{phoneSheets}
		</div>
	);
};

export default ReminderRow;
